import React from 'react'
import { InlineMath, BlockMath } from 'react-katex'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import 'katex/dist/katex.min.css'
import { escapeBareMathPercent, restoreBareLatexCommands } from '../utils/latexRepair'
import { isLikelyMath, findDollarMathExpressions } from '@shared/math/detection.js'
import { createLogger } from '../utils/logger'

const logger = createLogger('MathRenderer')

// Math-vs-currency detection lives in the shared module (CONTENT-5) so the client renderer and
// the server normalizer can never drift apart. Re-exported here because many consumers (and the
// contentRenderer pipeline) import these names from MathRenderer.
export { isLikelyMath, findDollarMathExpressions }

// Normalize a math source right at the KaTeX boundary: restore backslashes on bare
// LaTeX commands (`approx` → `\approx`), then escape bare `%` (a KaTeX comment char that
// would silently drop the rest of an expression). Covers every math source — markdown
// placeholders, runtime $...$ / \(...\), and block math — regardless of how it was
// extracted. Both steps are idempotent; prose is never touched.
const prepareMathSource = (math) => escapeBareMathPercent(restoreBareLatexCommands(math))
const SafeInlineMath = ({ math }) => <InlineMath math={prepareMathSource(math)} />
const SafeBlockMath = ({ math }) => <BlockMath math={prepareMathSource(math)} />

/**
 * Decode HTML entities that might appear in LaTeX content
 * This handles cases where content like "$x<7$" was HTML-encoded to "$x&lt;7$"
 */
export const decodeHTMLEntities = (text) => {
  if (!text) return text
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&le;/g, '≤')
    .replace(/&ge;/g, '≥')
}

// React Context to pass pre-extracted math map from renderMarkdownSegment to TextWithMath.
// This avoids ReactMarkdown mangling \$ escapes before math detection can see them.
const MathMapContext = React.createContext(null)

// Regex to find %%MATH_N%% placeholders inserted by renderMarkdownSegment
const PLACEHOLDER_RE = /%%MATH_(\d+)%%/g

/**
 * Render text by replacing %%MATH_N%% placeholders with InlineMath components.
 */
const renderPlaceholders = (text, mathMap) => {
  const parts = []
  let lastIdx = 0
  let m

  PLACEHOLDER_RE.lastIndex = 0
  while ((m = PLACEHOLDER_RE.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push(text.slice(lastIdx, m.index))
    }

    const mathContent = mathMap.get(m[0])
    if (mathContent !== undefined) {
      try {
        parts.push(
          <span key={m.index} className="not-prose">
            <SafeInlineMath math={mathContent} />
          </span>
        )
      } catch (error) {
        logger.error('Error rendering pre-extracted LaTeX:', error, { content: mathContent })
        parts.push(m[0])
      }
    } else {
      parts.push(m[0])
    }

    lastIdx = m.index + m[0].length
  }

  if (lastIdx < text.length) {
    parts.push(text.slice(lastIdx))
  }

  return parts.length > 0 && lastIdx > 0 ? parts : null
}

/**
 * Custom component for ReactMarkdown that renders text with inline LaTeX.
 * If a math map is provided via context, it renders %%MATH_N%% placeholders as InlineMath.
 * Otherwise falls back to runtime dollar-sign detection.
 */
const TextWithMath = ({ children }) => {
  if (typeof children !== 'string') {
    return <>{children}</>
  }

  // Decode HTML entities first
  const decodedText = decodeHTMLEntities(children)

  // Check context for pre-extracted math placeholders
  const mathMap = React.useContext(MathMapContext)
  if (mathMap && mathMap.size > 0) {
    const placeholderResult = renderPlaceholders(decodedText, mathMap)
    if (placeholderResult) {
      return <>{placeholderResult}</>
    }
  }

  // Fallback: runtime detection for \(...\) and $...$ patterns
  const parenRegex = /\\\(.+?\\\)/gs
  const validMatches = []
  let match

  while ((match = parenRegex.exec(decodedText)) !== null) {
    validMatches.push({
      start: match.index,
      end: match.index + match[0].length,
      content: match[0].slice(2, -2)
    })
  }

  const dollarMatches = findDollarMathExpressions(decodedText)
  validMatches.push(...dollarMatches)

  validMatches.sort((a, b) => a.start - b.start)

  const parts = []
  let lastIndex = 0

  for (const validMatch of validMatches) {
    if (validMatch.start < lastIndex) {
      continue
    }

    if (validMatch.start > lastIndex) {
      parts.push(decodedText.slice(lastIndex, validMatch.start))
    }

    try {
      // Wrap in not-prose to prevent Tailwind Typography from breaking KaTeX rendering
      parts.push(
        <span key={validMatch.start} className="not-prose">
          <SafeInlineMath math={validMatch.content} />
        </span>
      )
    } catch (error) {
      logger.error('Error rendering inline LaTeX:', error, { content: validMatch.content })
      parts.push(decodedText.slice(validMatch.start, validMatch.end))
    }

    lastIndex = validMatch.end
  }

  if (lastIndex < decodedText.length) {
    parts.push(decodedText.slice(lastIndex))
  }

  return parts.length > 0 ? <>{parts}</> : <>{decodedText}</>
}

/**
 * Render a markdown string through ReactMarkdown with inline LaTeX support.
 * Pre-extracts $...$ math expressions before ReactMarkdown can mangle \$ escapes.
 */
const renderMarkdownSegment = (text) => {
  if (!text) return null

  // Pre-extract math expressions before ReactMarkdown can mangle \$ escapes.
  // ReactMarkdown's CommonMark parser treats \$ as a backslash escape → outputs literal $,
  // which destroys patterns like $\$236$ (LaTeX for "$236").
  const mathExpressions = findDollarMathExpressions(text)
  let processedText = text
  const mathMap = new Map()

  // Replace math expressions with inert placeholders (process in reverse to preserve positions)
  for (let i = mathExpressions.length - 1; i >= 0; i--) {
    const expr = mathExpressions[i]
    const placeholder = `%%MATH_${i}%%`
    mathMap.set(placeholder, expr.content)
    processedText = processedText.slice(0, expr.start) + placeholder + processedText.slice(expr.end)
  }

  // Also extract \(...\) patterns to protect them from ReactMarkdown
  const parenRegex = /\\\(.+?\\\)/gs
  let parenMatch
  const parenMatches = []
  while ((parenMatch = parenRegex.exec(processedText)) !== null) {
    parenMatches.push(parenMatch)
  }
  // Process in reverse to preserve positions
  for (let i = parenMatches.length - 1; i >= 0; i--) {
    const pm = parenMatches[i]
    const idx = mathExpressions.length + i
    const placeholder = `%%MATH_${idx}%%`
    mathMap.set(placeholder, pm[0].slice(2, -2))
    processedText =
      processedText.slice(0, pm.index) + placeholder + processedText.slice(pm.index + pm[0].length)
  }

  // Ensure consecutive math-placeholder-only lines get paragraph breaks.
  // CommonMark treats a single \n as a soft break (space), which causes
  // stacked equations like "$y = 3x - 5$\n$2x + y = 10$" to render
  // side-by-side instead of vertically.
  if (mathMap.size > 0) {
    const lines = processedText.split('\n')
    const result = []
    for (let i = 0; i < lines.length; i++) {
      result.push(lines[i])
      if (
        i < lines.length - 1 &&
        /^\s*%%MATH_\d+%%\s*$/.test(lines[i]) &&
        /^\s*%%MATH_\d+%%\s*$/.test(lines[i + 1])
      ) {
        result.push('') // blank line = paragraph break
      }
    }
    processedText = result.join('\n')
  }

  // Convert Unicode bullet points (•) at start of lines to markdown list syntax
  // These appear in Rhetorical Synthesis questions with "notes" format
  processedText = processedText.replace(/^• /gm, '- ')

  // Normalize escaped/over-escaped literal dollars. Real math was already pulled into
  // placeholders above, so any remaining `\$` / `\\$` is an escaped dollar amount the
  // author intended to show verbatim. CommonMark would collapse `\\` → `\` and leave a
  // stray `\$950`; reduce any run of backslashes before `$` to a plain literal `$`.
  processedText = processedText.replace(/\\+\$/g, '$')

  return (
    <MathMapContext.Provider value={mathMap}>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
        components={{
          // Override text rendering to handle LaTeX within text nodes
          text: TextWithMath,
          // Inline code may wrap pre-extracted %%MATH_n%% placeholders (math written
          // inside backticks). Restore them to rendered KaTeX instead of leaking the raw
          // placeholder; non-math code falls through to a normal <code>.
          code: ({ children, ...props }) => {
            if (mathMap.size > 0 && typeof children === 'string') {
              const restored = renderPlaceholders(children, mathMap)
              if (restored) return <>{restored}</>
            }
            return <code {...props}>{children}</code>
          },
          // Also handle paragraphs, list items, etc. that contain text
          p: ({ children }) => <p>{processChildren(children)}</p>,
          li: ({ children }) => <li>{processChildren(children)}</li>,
          strong: ({ children }) => <strong>{processChildren(children)}</strong>,
          em: ({ children }) => <em>{processChildren(children)}</em>,
          // Table cells: process LaTeX in table content
          td: ({ children, ...props }) => (
            <td {...props} className="border border-gray-300 dark:border-gray-600 px-4 py-1.5">
              {processChildren(children)}
            </td>
          ),
          th: ({ children, ...props }) => (
            <th
              {...props}
              className="border border-gray-300 dark:border-gray-600 px-4 py-1.5 font-semibold"
            >
              {processChildren(children)}
            </th>
          ),
          // Styled table elements for consistent appearance
          table: ({ children }) => (
            <div className="overflow-x-auto my-4">
              <table className="border-collapse border border-gray-300 dark:border-gray-600 text-sm">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-gray-100 dark:bg-gray-700">{children}</thead>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">{children}</tbody>
          )
        }}
      >
        {processedText}
      </ReactMarkdown>
    </MathMapContext.Provider>
  )
}

// Pattern to find block-level math: $$...$$ or \[...\]
// Uses non-greedy matching to handle multiple blocks
const BLOCK_MATH_PATTERN = /\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]/g

export const renderMarkdownWithMath = (content) => {
  if (!content) return null

  // Decode HTML entities in the full content first
  const decodedContent = decodeHTMLEntities(content)

  // Check for block-level math ($$...$$ or \[...\])
  // Split content into alternating text and block-math segments
  const blockMatches = []
  let match
  BLOCK_MATH_PATTERN.lastIndex = 0
  while ((match = BLOCK_MATH_PATTERN.exec(decodedContent)) !== null) {
    blockMatches.push({
      start: match.index,
      end: match.index + match[0].length,
      math: (match[1] || match[2]).trim()
    })
  }

  // No block math found: render everything as markdown with inline math
  if (blockMatches.length === 0) {
    return renderMarkdownSegment(decodedContent)
  }

  // Split into segments: text → block math → text → block math → ...
  const parts = []
  let lastIndex = 0

  for (let i = 0; i < blockMatches.length; i++) {
    const bm = blockMatches[i]

    // Text before this block math
    const textBefore = decodedContent.slice(lastIndex, bm.start).trim()
    if (textBefore) {
      parts.push(
        <React.Fragment key={`md-${i}`}>{renderMarkdownSegment(textBefore)}</React.Fragment>
      )
    }

    // The block math itself — wrap in not-prose to prevent Tailwind Typography interference
    try {
      parts.push(
        <div key={`bm-${i}`} className="not-prose">
          <SafeBlockMath math={bm.math} />
        </div>
      )
    } catch (error) {
      logger.error('Error rendering block LaTeX:', error, { math: bm.math })
      parts.push(
        <pre key={`bm-err-${i}`} className="text-red-500 text-sm overflow-x-auto">
          {bm.math}
        </pre>
      )
    }

    lastIndex = bm.end
  }

  // Remaining text after the last block math
  const trailing = decodedContent.slice(lastIndex).trim()
  if (trailing) {
    parts.push(<React.Fragment key="md-trailing">{renderMarkdownSegment(trailing)}</React.Fragment>)
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>
}

/**
 * Process children nodes, rendering LaTeX in text nodes
 */
const processChildren = (children) => {
  if (!children) return children

  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      return <TextWithMath>{child}</TextWithMath>
    }
    return child
  })
}

/**
 * MathRenderer component for rendering LaTeX equations
 * Supports both inline and block math expressions
 */
const MathRenderer = ({ content, inline = false }) => {
  if (!content) return null

  // Decode HTML entities before rendering
  const decodedContent = decodeHTMLEntities(content)

  try {
    if (inline) {
      return (
        <span className="not-prose">
          <SafeInlineMath math={decodedContent} />
        </span>
      )
    } else {
      return (
        <div className="not-prose">
          <SafeBlockMath math={decodedContent} />
        </div>
      )
    }
  } catch (error) {
    logger.error('Error rendering LaTeX:', error)
    return (
      <span
        className="inline-block border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded px-2 py-0.5 text-xs text-red-600 dark:text-red-400 cursor-help"
        title={`LaTeX error: ${error?.message || 'Parse error'}\nSource: ${content}`}
      >
        <span className="font-mono">
          {content.length > 60 ? content.slice(0, 57) + '...' : content}
        </span>
      </span>
    )
  }
}

/**
 * Enhanced content renderer that detects and renders LaTeX expressions
 */
export const renderMathContent = (content) => {
  if (!content) return null

  // Find \(...\) and \[...\] patterns using regex
  const parenRegex = /\\\((.+?)\\\)|\\\[([^\]]+)\\\]/gs
  const validMatches = []
  let match

  while ((match = parenRegex.exec(content)) !== null) {
    if (match[1]) {
      // Inline: \(...\)
      validMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        content: match[1],
        isBlock: false
      })
    } else if (match[2]) {
      // Block: \[...\]
      validMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        content: match[2],
        isBlock: true
      })
    }
  }

  // Find $...$ patterns using our smart algorithm
  const dollarMatches = findDollarMathExpressions(content)
  for (const dm of dollarMatches) {
    validMatches.push({
      start: dm.start,
      end: dm.end,
      content: dm.content,
      isBlock: false
    })
  }

  // Sort by start position
  validMatches.sort((a, b) => a.start - b.start)

  // Build output, handling non-overlapping matches
  const parts = []
  let lastIndex = 0

  for (const validMatch of validMatches) {
    // Skip if this match overlaps with already processed content
    if (validMatch.start < lastIndex) {
      continue
    }

    // Add text before the LaTeX expression
    if (validMatch.start > lastIndex) {
      const textContent = content.slice(lastIndex, validMatch.start)
      if (textContent) {
        parts.push(<span key={`text-${lastIndex}`}>{textContent}</span>)
      }
    }

    parts.push(
      <MathRenderer
        key={`math-${validMatch.start}`}
        content={validMatch.content}
        inline={!validMatch.isBlock}
      />
    )

    lastIndex = validMatch.end
  }

  // Add remaining text after the last LaTeX expression
  if (lastIndex < content.length) {
    const remainingText = content.slice(lastIndex)
    if (remainingText) {
      parts.push(<span key={`text-${lastIndex}`}>{remainingText}</span>)
    }
  }

  // If no LaTeX expressions were found, return the original content
  if (parts.length === 0) {
    return content
  }

  return <span>{parts}</span>
}

export default MathRenderer
